import React, { useState } from 'react';
import classNames from 'classnames';

import { DashboardMenu, DashboardMenuItem } from '../../components';

import css from './DashboardTabBar.module.css';

/**
 * The secondary tab bar used on the dashboards.
 *
 * One presentation at every width: a single control showing the current
 * section, with the rest behind its menu. It used to render a horizontal strip
 * of every tab above a breakpoint and collapse to a dropdown only on phones.
 * Showing one section at a time everywhere keeps the bar the same shape on a
 * phone and a desktop, and removes the last width at which a row of tabs could
 * be cramped or clipped.
 *
 * The trade is a click to reach a section you can no longer see, so the control
 * always names the current one and the menu marks it with aria-current.
 *
 * The dropdown is a DashboardMenu, which unmounts its panel when closed, puts
 * aria-haspopup and aria-expanded on the trigger, and handles arrow keys, Home,
 * End and Escape. The bare Menu this replaced did none of that.
 *
 * `action` is rendered outside the tab semantics on purpose: on both dashboards
 * the "details" entry navigates to another page rather than switching the view
 * behind the bar, so it does not belong in a "which view am I looking at"
 * picker.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Extends the default root class
 * @param {number} [props.progress] - Lifecycle completion, 0-100, drawn as the green bar
 * @param {Array<Object>} props.tabs - [{ id: string, label: node, onSelect: Function }]
 * @param {string} [props.selectedTabId] - The id of the currently shown tab
 * @param {React.ReactNode} [props.action] - Navigation control shown beside the tabs
 * @param {string} [props.ariaLabel] - Names the control for assistive tech
 * @returns {JSX.Element}
 */
const DashboardTabBar = props => {
  const {
    className,
    progress = 0,
    tabs = [],
    selectedTabId,
    action,
    ariaLabel = 'Select a view',
  } = props;
  const [isOpen, setIsOpen] = useState(false);

  const selectedTab = tabs.find(t => t.id === selectedTabId) || tabs[0];
  // Clamp so a bad caller can never draw the bar past the end of the track.
  const progressWidth = `${Math.max(0, Math.min(100, progress))}%`;

  const select = tab => {
    setIsOpen(false);
    if (tab.onSelect) {
      tab.onSelect();
    }
  };

  return (
    <div className={classNames(css.root, className)}>
      <div className={css.bar}>
        <DashboardMenu
          className={css.menu}
          triggerClassName={css.menuTrigger}
          variant="nav"
          align="left"
          ariaLabel={ariaLabel}
          label={selectedTab ? selectedTab.label : null}
          isOpen={isOpen}
          onToggleActive={setIsOpen}
        >
          {tabs.map(tab => (
            <DashboardMenuItem
              key={tab.id}
              selected={tab.id === selectedTabId}
              onClick={() => select(tab)}
            >
              {tab.label}
            </DashboardMenuItem>
          ))}
        </DashboardMenu>
        {action ? <div className={css.barAction}>{action}</div> : null}
        <span className={css.progress} style={{ width: progressWidth }} />
      </div>
    </div>
  );
};

export default DashboardTabBar;
